import { IMediaItem } from 'types/media'
import { videoTypes } from 'consts'
import { videoThumbsService } from 'services/video-thumbs'
import { getMediaSourceTrimStartSeconds } from 'utils/media'
import { FileRegistry } from './file-registry/FileRegistry'

const MAX_FILE_SIZE_BYTES = 200 * 1024 // 200KB
const MAX_IMAGE_WIDTH = 1920
const MAX_IMAGE_HEIGHT = 1080
const COMPRESSION_QUALITY = 0.85

/**
 * Loads an image from a File using Object URL.
 */
const loadImageFromFile = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const fileRegistry = FileRegistry.getInstance()
    const url = fileRegistry.registerFile(file)
    const img = new Image()

    img.onload = () => {
      fileRegistry.deleteFile(url)
      resolve(img)
    }

    img.onerror = error => {
      fileRegistry.deleteFile(url)
      reject(error)
    }

    img.src = url
  })
}

/**
 * Compresses an image file only if it exceeds the defined size limit.
 * Returns the original file when compression is unnecessary or fails.
 */
export const compressImageIfNeeded = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/')) {
    return file
  }

  if (file.size <= MAX_FILE_SIZE_BYTES) {
    return file
  }

  try {
    const img = await loadImageFromFile(file)

    const originalWidth = img.width
    const originalHeight = img.height

    //Calculate scale factor without upscaling
    const scale = Math.min(
      MAX_IMAGE_WIDTH / originalWidth,
      MAX_IMAGE_HEIGHT / originalHeight,
      1
    )

    const width = Math.round(originalWidth * scale)
    const height = Math.round(originalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return file
    }

    ctx.drawImage(img, 0, 0, width, height)

    const blob = await canvasToJpegBlob(canvas, COMPRESSION_QUALITY)

    // If compression does not reduce size — return original file
    if (blob.size >= file.size) {
      return file
    }

    return new File([blob], file.name, {
      type: 'image/jpeg',
      lastModified: file.lastModified
    })
  } catch {
    return file
  }
}

/**
 * Converts a canvas to a JPEG Blob wrapped in a Promise.
 */
const canvasToJpegBlob = (
  canvas: HTMLCanvasElement,
  quality: number
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          return reject(new Error('Failed to produce JPEG blob'))
        }
        resolve(blob)
      },
      'image/jpeg',
      quality
    )
  })
}

/**
 * Calculates frame timestamp for a media item based on trimStart.
 * For images: returns 0.
 */
export const calculateFrameIndex = (mediaItem: IMediaItem): number => {
  const isVideo = videoTypes.includes(mediaItem.type)

  if (!isVideo) return 0

  return Math.floor(getMediaSourceTrimStartSeconds(mediaItem))
}

/**
 * Generates a thumbnail File from a media item using the cached video thumbs.
 * Returns null if no thumb has been cached yet for this asset.
 */
export const generateThumbnailFromMediaItem = async (
  mediaItem: IMediaItem
): Promise<File | null> => {
  const { type, meta, assetId } = mediaItem

  const isVideo = videoTypes.includes(type)

  if (!isVideo) {
    const path = meta.previewPath || meta.path || ''
    if (!path) return null
    try {
      const response = await fetch(path, {
        cache: 'no-store'
      })
      const blob = await response.blob()
      return new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' })
    } catch {
      return null
    }
  }

  const cached = videoThumbsService.getCachedUrls(assetId)
  if (cached.size === 0) return null

  const targetTimestamp = Math.floor(getMediaSourceTrimStartSeconds(mediaItem))

  const url =
    cached.get(targetTimestamp) ??
    cached.get(
      Array.from(cached.keys()).reduce((closest, ts) =>
        Math.abs(ts - targetTimestamp) < Math.abs(closest - targetTimestamp)
          ? ts
          : closest
      )
    )

  if (!url) return null

  try {
    const response = await fetch(url)
    const blob = await response.blob()
    return new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' })
  } catch {
    return null
  }
}
